import React, { useState, useCallback, useEffect } from "react";
import { View } from "react-native";
import { extractApiError } from "@/lib/api/errors";
import { Modal } from "@/components/ui/Modal";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { SuccessState } from "@/components/ui/SuccessState";
import { ErrorState } from "@/components/ui/ErrorState";
import { useI18n } from "@/context/I18nContext";

export interface EditTranslationModalProps {
  visible: boolean;
  onDismiss: () => void;
  /** The translation key being edited (e.g. "common.save"). */
  translationKey?: string;
  /** Pre-filled values for each locale. */
  initialEn?: string;
  initialZhCn?: string;
  initialAr?: string;
  /**
   * When set, only shows the input for that locale (single-field mode).
   * When undefined, shows all three fields.
   */
  locale?: "en" | "zh-CN" | "ar";
  /**
   * Called after user presses Save with the updated values.
   * No-op by default; API wiring is tracked in #400.
   */
  onSave?: (values: { en: string; zhCn: string; ar: string }) => void;
}

type State = "form" | "success" | "error";

/**
 * Modal for editing EN / ZH-CN / AR translations for a single key.
 *
 * Matches Figma section "Localization / Translation Details / Edit One Translation".
 * The submit handler is a no-op place-holder until #400 wires the PATCH endpoint.
 */
export function EditTranslationModal({
  visible,
  onDismiss,
  translationKey,
  initialEn = "",
  initialZhCn = "",
  initialAr = "",
  locale,
  onSave,
}: EditTranslationModalProps) {
  const { t } = useI18n();

  const [state, setState] = useState<State>("form");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [en, setEn] = useState(initialEn);
  const [zhCn, setZhCn] = useState(initialZhCn);
  const [ar, setAr] = useState(initialAr);

  // Reset state when modal opens with new initial values
  useEffect(() => {
    if (visible) {
      setState("form");
      setErrorMessage(null);
      setEn(initialEn);
      setZhCn(initialZhCn);
      setAr(initialAr);
    }
  }, [visible, initialEn, initialZhCn, initialAr]);

  const handleDismiss = useCallback(() => {
    setState("form");
    onDismiss();
  }, [onDismiss]);

  const handleSave = useCallback(async () => {
    try {
      await Promise.resolve(onSave?.({ en, zhCn, ar }));
      setState("success");
    } catch (err) {
      setErrorMessage(extractApiError(err, ""));
      setState("error");
    }
  }, [en, zhCn, ar, onSave]);

  if (state === "success") {
    return (
      <Modal visible={visible} onDismiss={handleDismiss} useScrollView={false}>
        <SuccessState
          title="staff.localization.modal.saveSuccess"
          description="staff.localization.modal.saveSuccessBody"
        />
      </Modal>
    );
  }

  if (state === "error") {
    return (
      <Modal visible={visible} onDismiss={handleDismiss} useScrollView={false}>
        <ErrorState
          title="staff.localization.modal.saveError"
          description="staff.localization.modal.saveErrorBody"
          descriptionText={errorMessage || undefined}
          onRetry={() => setState("form")}
          retryLabel="errorState.retry"
          onSecondaryAction={handleDismiss}
          secondaryActionLabel="common.cancel"
        />
      </Modal>
    );
  }

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      title={"staff.localization.modal.title"}
      useScrollView
      footer={
        <View className="flex-row justify-end gap-3">
          <Button
            label="staff.localization.modal.cancel"
            variant="tertiary"
            onPress={handleDismiss}
          />

          <Button label="staff.localization.modal.save" variant="primary" onPress={handleSave} />
        </View>
      }
    >
      {/* Key title — blue highlighted box */}
      {!!translationKey && (
        <View className="w-full items-start mb-5 bg-blue-5 border-blue-50 rounded-lg px-4 py-3">
          <AppText weight="medium" className="text-sm text-blue-50 uppercase tracking-wider mb-1">
            {t("staff.localization.modal.keyLabel")}
          </AppText>
          <AppText weight="medium" className="text-lg text-dark-text-50">
            {translationKey}
          </AppText>
        </View>
      )}

      {/* EN field */}
      {(!locale || locale === "en") && (
        <AppTextInput
          label="staff.localization.modal.englishLabel"
          value={en}
          onChangeText={setEn}
          containerClassName={locale ? "mb-0" : "mb-4"}
        />
      )}

      {/* AR field */}
      {(!locale || locale === "ar") && (
        <AppTextInput
          label="staff.localization.modal.arabicLabel"
          value={ar}
          onChangeText={setAr}
          containerClassName={locale ? "mb-0" : "mb-4"}
          isRTL
        />
      )}

      {/* ZH-CN field */}
      {(!locale || locale === "zh-CN") && (
        <AppTextInput
          label="staff.localization.modal.chineseLabel"
          value={zhCn}
          onChangeText={setZhCn}
          containerClassName="mb-0"
        />
      )}
    </Modal>
  );
}
